import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { formatIsoDate } from '../../schedule/lib/format'
import DetailRow from '../components/DetailRow'
import { formatMoney } from '../lib/money'
import { isRfqOverdue, rfqTypeText } from '../lib/rfq'
import type { CostRfq } from '../types'

const DASH = '—'

interface Props { rfq: CostRfq; projectId: string; currency: string; locale: string; today: string }

/** Part D spec §8: the brief — type, budget item, due date, description — and, once awarded, the winner and the result. */
const RfqSummaryCard: React.FC<Props> = ({ rfq, projectId, currency, locale, today }) => {
  const { t } = useTranslation()
  const base = `/project/${projectId}/cost-control`
  const when = (name: string | null, at: string | null) => (at ? `${name ?? DASH} · ${new Date(at).toLocaleString(t('date_locale') || 'cs-CZ')}` : DASH)
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader><CardTitle className="text-base">{t('CostControl_rfq_details_title')}</CardTitle></CardHeader>
      <CardContent>
        <dl className="divide-y">
          <DetailRow label={t('CostControl_rfq_field_type')}>
            {rfq.contractId ? <Link className="text-primary hover:underline" to={`${base}/contracts/${rfq.contractId}`}>{rfqTypeText(rfq, t)}</Link> : rfqTypeText(rfq, t)}
          </DetailRow>
          {rfq.sourceChangeId && (
            <DetailRow label={t('CostControl_change_kind_POTENTIAL')}>
              <Link className="text-primary hover:underline" to={`${base}/changes/${rfq.sourceChangeId}`}>{t('CostControl_rfq_from_potential', { number: rfq.sourceChangeNumber })}</Link>
            </DetailRow>
          )}
          <DetailRow label={t('CostControl_rfq_field_node')}>
            {rfq.nodeName ? (
              <>
                {rfq.nodePath && <span className="text-muted-foreground">{rfq.nodePath} / </span>}
                {rfq.nodeName}
              </>
            ) : <span className="text-muted-foreground">{t('CostControl_rfq_node_none')}</span>}
          </DetailRow>
          <DetailRow label={t('CostControl_rfq_field_dueDate')}>
            <span className={cn(isRfqOverdue(rfq, today) && 'font-medium text-destructive')}>{rfq.dueDate ? formatIsoDate(t, rfq.dueDate) : DASH}</span>
          </DetailRow>
          <DetailRow label={t('CostControl_rfq_field_description')}><span className="whitespace-pre-wrap">{rfq.description || DASH}</span></DetailRow>
          {rfq.status === 'AWARDED' && (
            <>
              <DetailRow label={t('CostControl_rfq_col_winner')}>
                {rfq.winnerName ?? DASH}
                {rfq.winnerAmount && <span className="tabular-nums"> · {formatMoney(rfq.winnerAmount, { locale, currency })}</span>}
              </DetailRow>
              <DetailRow label={t('CostControl_rfq_field_result')}>
                {rfq.awardedContractId ? <Link className="text-primary hover:underline" to={`${base}/contracts/${rfq.awardedContractId}`}>{t('CostControl_rfq_result_contract')}</Link>
                  : rfq.awardedChangeId ? <Link className="text-primary hover:underline" to={`${base}/changes/${rfq.awardedChangeId}`}>{t('CostControl_rfq_result_change', { number: rfq.awardedChangeNumber })}</Link>
                    : <span className="text-muted-foreground">{t('CostControl_rfq_result_gone')}</span>}
              </DetailRow>
              <DetailRow label={t('CostControl_rfq_field_awarded')}>{when(rfq.awardedByName, rfq.awardedAt)}</DetailRow>
            </>
          )}
          <DetailRow label={t('CostControl_rfq_field_created')}>{when(rfq.createdByName, rfq.createdAt)}</DetailRow>
        </dl>
      </CardContent>
    </Card>
  )
}
export default RfqSummaryCard
